import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Users, LogOut, KeyRound, Leaf } from 'lucide-react';
import WellnessJournalPage from './WellnessJournalPage';
import IntakeReview from '../components/IntakeReview';
import ClientQuizPanel from '../components/ClientQuizPanel';
import ClientFastBrainPanel from '../components/ClientFastBrainPanel';
import CoachingResourceManager from '../components/CoachingResourceManager';
import { VipSwitcher } from './CoachingPage';
import { journalCategories } from '../data/journalChecklist';
import { categories as vipVideoCategories } from './VIPPage';
import { ballVideoChecklist } from '../components/StabilityBallProgressions';
import { wellnessResources } from '../data/resources';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Jill's Corner reuses the Journal engine on a separate "board".
const ORDER = ['feldenkrais', 'core-tests', 'core-restoration', 'back-reset'];
const baseCategories = ORDER
  .map((id) => journalCategories.find((c) => c.id === id))
  .filter(Boolean);

// Mirror the VIP video library into tickable checklist categories (same titles).
// Only real loaded videos (with a videoUrl) become tick items; auto-syncs as videos are added.
// Each item carries its videoUrl so the exercise name links straight to the video in the plan.
const videoCategories = vipVideoCategories
  .map((cat) => {
    const items = cat.videos
      .filter((v) => v.videoUrl)
      .map((v, i) => ({ id: `vipvid-${cat.id}-${i}`, label: v.title, videoUrl: v.videoUrl }));
    return items.length
      ? { id: `vipvideos-${cat.id}`, name: cat.title, blurb: 'Tick the videos we explored together.', layout: 'list', items }
      : null;
  })
  .filter(Boolean);

// Articles & readings Jill can assign — each links straight to the article in the client's plan.
const articlesCategory = {
  id: 'articles-readings',
  name: 'Articles & Readings',
  blurb: 'Tick an article to share it — it links right to the reading in your client\u2019s plan.',
  layout: 'list',
  items: wellnessResources.map((r) => ({
    id: `article-${r.id}`,
    label: r.title,
    articleUrl: `/resources/wellness?resource=${r.id}`,
  })),
};

const cornerCategories = [...baseCategories, ...videoCategories, ...ballVideoChecklist, articlesCategory];
export { cornerCategories };

const CORNER_PROPS = {
  board: 'jills-corner',
  activeTab: 'jills-corner',
  categories: cornerCategories,
  eyebrow: 'Practitioner Library',
  heading: "Jill's Corner",
  intro: 'A dated log for each session — tick what we worked on, assign homework, and add notes. Everything saves to the profile automatically.',
};

// Small reusable practitioner sign-in form (defined outside to keep focus stable)
const PractitionerLogin = ({ onSubmit, error, onCancel }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(email.trim(), password); }}
      className="space-y-3 text-left"
      data-testid="practitioner-login-form"
    >
      <div>
        <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Admin email</label>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
          data-testid="practitioner-email"
        />
      </div>
      <div>
        <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Admin password</label>
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
          data-testid="practitioner-password"
        />
      </div>
      {error && <p className="text-xs font-body text-red-600" data-testid="practitioner-login-error">{error}</p>}
      <div className="flex items-center gap-3 pt-1">
        <button type="submit" className="bg-forest text-cream font-body text-sm font-medium px-5 py-2 rounded-full hover:bg-forest-dark transition-colors" data-testid="practitioner-login-submit">
          Enter practitioner mode
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-xs font-body text-stone-400 hover:text-forest">Cancel</button>
        )}
      </div>
    </form>
  );
};

const JillsCornerPage = () => {
  const { user, hasAccess } = useAuth();
  const [adminAuth, setAdminAuth] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('gw_admin_auth') || 'null'); } catch { return null; }
  });
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginErr, setLoginErr] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [busyEmail, setBusyEmail] = useState('');
  const [clientQuery, setClientQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [coachingOpen, setCoachingOpen] = useState(false);

  const fetchClients = useCallback(async (auth) => {
    const a = auth || adminAuth;
    if (!a) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users?admin_email=${encodeURIComponent(a.email)}&admin_password=${encodeURIComponent(a.password)}`);
      if (res.ok) {
        const data = await res.json();
        const list = (data || [])
          .filter((u) => u.email && (u.is_admin || (u.unlocked_programs || []).includes('vip')))
          .sort((x, y) => (x.name || x.email).localeCompare(y.name || y.email));
        setClients(list);
        setAllUsers((data || [])
          .filter((u) => u.email && !u.is_admin)
          .sort((x, y) => (x.name || x.email).localeCompare(y.name || y.email)));
      }
    } catch (e) { console.error('Failed to load clients', e); }
  }, [adminAuth]);

  useEffect(() => { if (adminAuth) fetchClients(); }, [adminAuth, fetchClients]);

  const doLogin = async (email, password) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const auth = { email, password };
        sessionStorage.setItem('gw_admin_auth', JSON.stringify(auth));
        setAdminAuth(auth);
        setShowLogin(false);
        setLoginErr('');
      } else {
        setLoginErr("Those admin details didn't match.");
      }
    } catch (e) { setLoginErr('Sign-in failed. Please try again.'); }
  };

  const exitPractitioner = () => {
    sessionStorage.removeItem('gw_admin_auth');
    setAdminAuth(null);
    setSelectedClient(null);
    setClients([]);
    setAllUsers([]);
    setManageOpen(false);
  };

  const toggleVip = async (email, isIn) => {
    const a = adminAuth;
    if (!a) return;
    setBusyEmail(email);
    const action = isIn ? 'lock-program' : 'unlock-program';
    try {
      await fetch(`${BACKEND_URL}/api/admin/${action}?admin_email=${encodeURIComponent(a.email)}&admin_password=${encodeURIComponent(a.password)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: email, program_id: 'vip' }),
      });
      await fetchClients();
    } catch (e) { console.error('Failed to update client list', e); }
    setBusyEmail('');
  };

  const labelFor = (c) => {
    const isSelf = adminAuth && c.email && c.email.toLowerCase() === adminAuth.email.toLowerCase();
    return c.name ? `${c.name}${isSelf ? ' (You)' : ''} — ${c.email}` : `${c.email}${isSelf ? ' (You)' : ''}`;
  };
  const filteredClients = clients.filter((c) => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const clientBar = (
    <div className="mb-6" data-testid="practitioner-bar">
      <div className="bg-white border border-[#e2d4bf] rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-body uppercase tracking-wider text-[#8a6a5a]">
          <Sparkles size={13} /> Practitioner mode
        </span>
        <span className="font-body text-sm text-stone-600">Working on:</span>
        <div className="relative min-w-[260px]" data-testid="client-picker">
          <input
            type="text"
            value={pickerOpen ? clientQuery : (selectedClient ? labelFor(selectedClient) : '')}
            onChange={(e) => { setClientQuery(e.target.value); setPickerOpen(true); }}
            onFocus={() => { setPickerOpen(true); setClientQuery(''); }}
            onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
            placeholder="Search clients by name or email…"
            className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
            data-testid="client-search-input"
          />
          {pickerOpen && (
            <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-white border border-stone-200 rounded-lg shadow-lg" data-testid="client-options">
              {filteredClients.length === 0 && (
                <p className="px-3 py-2 text-xs text-stone-400 font-body">No matches</p>
              )}
              {filteredClients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setSelectedClient(c); setPickerOpen(false); setClientQuery(''); }}
                  className="block w-full text-left px-3 py-2 text-sm font-body text-forest hover:bg-stone-50"
                  data-testid={`client-option-${c.email}`}
                >
                  {labelFor(c)}
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedClient && (
          <button
            onClick={() => { setSelectedClient(null); setClientQuery(''); }}
            className="text-xs font-body text-stone-400 hover:text-forest"
            data-testid="client-clear"
          >
            Clear
          </button>
        )}
        <button
          onClick={() => setManageOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-body text-stone-500 hover:text-forest transition-colors"
          data-testid="manage-clients-toggle"
        >
          <Users size={13} /> Add / remove clients
        </button>
        <button
          onClick={() => setCoachingOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-body text-stone-500 hover:text-forest transition-colors"
          data-testid="coaching-manager-toggle"
        >
          <Leaf size={13} /> Coaching resources
        </button>
        <button onClick={exitPractitioner} className="ml-auto inline-flex items-center gap-1.5 text-xs font-body text-stone-400 hover:text-forest transition-colors" data-testid="practitioner-exit">
          <LogOut size={13} /> Exit
        </button>
      </div>

      {coachingOpen && <CoachingResourceManager auth={adminAuth} onClose={() => setCoachingOpen(false)} />}

      {manageOpen && (
        <div className="mt-2 bg-white border border-[#e2d4bf] rounded-xl p-4" data-testid="manage-clients-panel">
          <p className="font-body text-sm text-forest font-medium mb-0.5">Add or remove clients</p>
          <p className="font-body text-xs text-stone-500 mb-3">Adding a client puts them in the list above and gives them VIP access. Removing takes them out. (You always appear here yourself.)</p>
          <div className="space-y-1 max-h-72 overflow-auto">
            {allUsers.length === 0 && <p className="text-xs text-stone-400 font-body">No members yet.</p>}
            {allUsers.map((u) => {
              const isIn = (u.unlocked_programs || []).includes('vip');
              const busy = busyEmail === u.email;
              return (
                <div key={u.id || u.email} className="flex items-center gap-3 py-1.5 border-b border-stone-100 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-forest truncate">{u.name || u.email}</p>
                    <p className="font-body text-xs text-stone-400 truncate">{u.email}</p>
                  </div>
                  <button
                    onClick={() => toggleVip(u.email, isIn)}
                    disabled={busy}
                    className={`inline-flex items-center gap-1.5 text-xs font-body px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${isIn ? 'bg-emerald-100 text-emerald-800 hover:bg-red-100 hover:text-red-700' : 'bg-stone-100 text-stone-600 hover:bg-forest hover:text-cream'} ${busy ? 'opacity-50 cursor-wait' : ''}`}
                    data-testid={`manage-toggle-${u.email}`}
                  >
                    {busy ? 'Saving…' : isIn ? 'In list · Remove' : 'Add to list'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ── Practitioner mode ──
  if (adminAuth) {
    if (selectedClient) {
      const clientHeader = (
        <>
          {clientBar}
          <IntakeReview clientId={selectedClient.id} clientName={selectedClient.name || selectedClient.email} />
          <ClientQuizPanel auth={adminAuth} clientEmail={selectedClient.email} />
          <ClientFastBrainPanel auth={adminAuth} clientId={selectedClient.id} />
        </>
      );
      return (
        <WellnessJournalPage
          {...CORNER_PROPS}
          ownerId={selectedClient.id}
          ownerEmail={selectedClient.email}
          ownerName={selectedClient.name || ''}
          bypassGate
          practitioner
          eyebrow={`Practitioner Library · ${selectedClient.name || selectedClient.email}`}
          headerSlot={clientHeader}
        />
      );
    }
    // No client chosen yet — selection screen
    return (
      <div className="min-h-screen bg-cream py-12 px-4" data-testid="jills-corner-select">
        <div className="max-w-3xl mx-auto">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-6">
            <ArrowLeft size={16} /> Back to Programs
          </Link>
          <VipSwitcher active="jills-corner" />
          {clientBar}
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-forest/10 text-forest mb-4">
              <Users size={26} />
            </div>
            <h1 className="font-heading text-2xl text-forest mb-2">Choose a client to begin</h1>
            <p className="font-body text-stone-600 text-sm max-w-md mx-auto">
              Pick a client above and you'll be working directly on their profile — log a session, tick exercises,
              assign homework, and add notes. It all saves to their account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Member mode: their own board (with a discreet practitioner entry) ──
  if (user && hasAccess('vip')) {
    const practitionerLink = (
      <div className="mb-6" data-testid="practitioner-entry">
        {showLogin ? (
          <div className="w-full max-w-sm bg-white border border-stone-200 rounded-xl p-4" data-testid="practitioner-login-panel">
            <p className="font-heading text-sm text-forest mb-2">Practitioner sign-in</p>
            <PractitionerLogin onSubmit={doLogin} error={loginErr} onCancel={() => { setShowLogin(false); setLoginErr(''); }} />
          </div>
        ) : (
          <button
            onClick={() => setShowLogin(true)}
            className="w-full flex items-center justify-between gap-3 bg-forest/5 border border-forest/20 rounded-xl px-4 py-3 hover:bg-forest/10 transition-colors text-left"
            data-testid="practitioner-signin-link"
          >
            <span className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-forest/10 text-forest flex-shrink-0">
                <Users size={16} />
              </span>
              <span>
                <span className="block font-heading text-sm text-forest">Working with a client?</span>
                <span className="block font-body text-xs text-stone-500">Enter practitioner mode to choose a client and log their session</span>
              </span>
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-body text-forest flex-shrink-0"><KeyRound size={13} /> Sign in</span>
          </button>
        )}
      </div>
    );
    return <WellnessJournalPage {...CORNER_PROPS} headerSlot={practitionerLink} />;
  }

  // ── Not a member and not signed in as practitioner: gate + practitioner sign-in ──
  return (
    <div className="min-h-screen bg-cream py-16 px-4" data-testid="jills-corner-gate">
      <div className="max-w-2xl mx-auto text-center">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-10">
          <ArrowLeft size={16} /> Back to Programs
        </Link>
        <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-6">
            <Sparkles size={28} />
          </div>
          <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3">Private Members Area</p>
          <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">Jill's Corner</h1>
          <p className="text-stone-600 font-body leading-relaxed mb-8">
            A private space open to <span className="text-forest font-medium">V.I.P. members and package holders</span>.
            Reach out and Jill will get you set up.
          </p>
          {showLogin ? (
            <div className="max-w-sm mx-auto bg-white border border-stone-200 rounded-xl p-5" data-testid="practitioner-login-panel">
              <p className="font-heading text-sm text-forest mb-3">Practitioner sign-in</p>
              <PractitionerLogin onSubmit={doLogin} error={loginErr} onCancel={() => { setShowLogin(false); setLoginErr(''); }} />
            </div>
          ) : (
            <button onClick={() => setShowLogin(true)} className="inline-flex items-center gap-1.5 text-sm font-body text-forest hover:underline" data-testid="practitioner-signin-link">
              <KeyRound size={14} /> I'm the practitioner
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JillsCornerPage;
